import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Users, Eye, X, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Motion";
import { useAuth } from "@/context/AuthContext";
import api, { fmtErr, openExternal } from "@/lib/api";
import { fmtHeight } from "@/lib/units";
import ImageField from "@/components/ImageField";
import RichTextarea from "@/components/RichTextarea";
import CategorySelect from "@/components/CategorySelect";
import PlansSection from "@/components/PlansSection";
import AffiliatePanel from "@/components/AffiliatePanel";
import DocumentsManager, { DocumentChips } from "@/components/DocumentsManager";
import ChangePassword from "@/components/ChangePassword";
import { TosAgreement } from "@/components/TalentPass";
import { usePageMeta } from "@/lib/seo";

const EMPTY_FORM = { title: "", brand: "", description: "", city: "", date: "", compensation: "", gender: "Any", min_height: "", image_url: "", categories: [], instagram_dm: "" };

const STATUS_STYLE = {
  draft: "border-white/25 text-white/50",
  pending_review: "border-accent text-accent",
  published: "border-white text-white",
  expired: "border-white/20 text-white/40",
  rejected: "border-white/10 text-white/30",
};

function CastingForm({ editing = null, onCreated }) {
  const [form, setForm] = useState(editing ? {
    title: editing.title || "", brand: editing.brand || "", description: editing.description || "",
    city: editing.city || "", date: editing.date || "", compensation: editing.compensation || "",
    gender: editing.gender || "Any", min_height: editing.min_height ?? "", image_url: editing.image_url || "",
    categories: editing.categories || [], instagram_dm: editing.instagram_dm || "",
  } : EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, min_height: form.min_height ? parseInt(form.min_height, 10) : null };
      if (editing) {
        await api.put(`/employer/castings/${editing.id}`, payload);
        toast.success("Casting updated.");
      } else {
        await api.post("/castings", payload);
        toast.success("Draft created — publish it to enter review.");
      }
      onCreated();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <form onSubmit={submit} className="panel p-8 mb-16" data-testid="new-casting-form">
      <p className="overline-label mb-8">{editing ? "Edit casting — changes to a live call go live immediately" : "New casting call — draft"}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
        <div><label className="overline-label !text-[10px] block mb-1">Title</label><input data-testid="casting-title-input" required value={form.title} onChange={set("title")} className="field" /></div>
        <div><label className="overline-label !text-[10px] block mb-1">Brand / house</label><input data-testid="casting-brand-input" required value={form.brand} onChange={set("brand")} className="field" /></div>
        <div><label className="overline-label !text-[10px] block mb-1">City</label><input data-testid="casting-city-input" required value={form.city} onChange={set("city")} className="field" /></div>
        <div><label className="overline-label !text-[10px] block mb-1">Date</label><input data-testid="casting-date-input" type="date" value={form.date} onChange={set("date")} className="field" /></div>
        <div><label className="overline-label !text-[10px] block mb-1">Compensation</label><input data-testid="casting-compensation-input" value={form.compensation} onChange={set("compensation")} className="field" placeholder="$2,500 / day" /></div>
        <div>
          <label className="overline-label !text-[10px] block mb-1">Seeking</label>
          <select data-testid="casting-gender-select" value={form.gender} onChange={set("gender")} className="field bg-background">
            <option>Any</option><option>Female</option><option>Male</option><option>Non-binary</option>
          </select>
        </div>
        <CategorySelect value={form.categories} onChange={(v) => setForm({ ...form, categories: v })} testPrefix="casting" />
        <div className="sm:col-span-2"><label className="overline-label !text-[10px] block mb-1">Instagram for DM applications (optional)</label><input data-testid="casting-instagram-input" value={form.instagram_dm} onChange={set("instagram_dm")} className="field" placeholder="@yourhouse — models can apply by Instagram DM" /></div>
        <div><label className="overline-label !text-[10px] block mb-1">Min height (cm)</label><input data-testid="casting-height-input" type="number" value={form.min_height} onChange={set("min_height")} className="field" /></div>
        <ImageField value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testPrefix="casting" />
        <div className="sm:col-span-2"><label className="overline-label !text-[10px] block mb-1">Description — Enter for a line break, blank line for a new paragraph; select text to format</label><RichTextarea testid="casting-description-input" required value={form.description} onChange={set("description")} className="field" placeholder={"The role and the story of the shoot…\nWhat you're looking for — look, experience, availability…\nDates, location and what's included…"} /></div>
      </div>
      <button data-testid="casting-create-button" disabled={busy} className="btn-primary mt-10 disabled:opacity-40">
        {busy ? "Saving…" : editing ? "Save changes" : "Create draft"}
      </button>
    </form>
  );
}

function ApplicantRow({ a, onStatus, showCasting = false, idPrefix = "applicant" }) {
  const [pendingAction, setPendingAction] = useState(null);
  const [note, setNote] = useState("");

  const send = () => {
    onStatus(a.id, pendingAction, note);
    setPendingAction(null);
    setNote("");
  };

  return (
    <div className="py-6 border-b border-white/10 last:border-0 grid grid-cols-1 sm:grid-cols-12 gap-4 items-center" data-testid={`${idPrefix}-${a.id}`}>
      <div className="sm:col-span-4 flex items-center gap-4">
        {a.model?.photo_url && (
          <Link to={`/models/${a.model?.slug || a.model_id}`} target="_blank" data-testid={`${idPrefix}-view-book-${a.id}`}>
            <img src={a.model.photo_url} alt={a.model_name} className="w-12 h-16 object-cover grayscale hover:grayscale-0 transition-all duration-300" />
          </Link>
        )}
        <div>
          <Link to={`/models/${a.model?.slug || a.model_id}`} target="_blank" data-testid={`${idPrefix}-view-book-link-${a.id}`} className="group/link">
            <p className="font-display text-xl group-hover/link:text-accent transition-colors">{a.model_name}</p>
            <p className="overline-label !text-[10px] mt-1">{a.model?.city} · {fmtHeight(a.model?.height_cm)}</p>
            <p className="font-grotesk text-[9px] uppercase tracking-[0.15em] text-accent mt-1">View book ↗</p>
          </Link>
          {showCasting && <p className="overline-label !text-[10px] mt-1 !text-accent">→ {a.casting_title}</p>}
        </div>
      </div>
      <div className="sm:col-span-5">
        <p className="text-sm text-muted-foreground leading-relaxed">{a.message}</p>
        {(a.replies || []).length > 0 && (
          <div className="mt-3 border-l-2 border-accent pl-3 flex flex-col gap-2" data-testid={`${idPrefix}-replies-${a.id}`}>
            {a.replies.map((r, i) => (
              <p key={i} className="text-sm text-accent/90 italic" data-testid={`${idPrefix}-reply-${a.id}-${i}`}>
                &ldquo;{r.message}&rdquo;
                <span className="not-italic font-grotesk text-[9px] uppercase tracking-[0.12em] text-white/40 ml-2">
                  — {a.model_name} · {new Date(r.at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </span>
              </p>
            ))}
          </div>
        )}
        {a.documents?.length > 0 && (
          <div className="mt-3">
            <DocumentChips docs={a.documents} />
          </div>
        )}
      </div>
      <div className="sm:col-span-3 flex gap-2 justify-end">
        {pendingAction ? (
          <div className="flex flex-col gap-2 w-full">
            <input
              data-testid={`${idPrefix}-note-${a.id}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="A note for the model (optional)"
              className="field !py-2 !text-xs"
            />
            <div className="flex gap-2 justify-end">
              <button data-testid={`${idPrefix}-note-send-${a.id}`} onClick={send} className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-accent text-accent hover:bg-accent hover:text-white px-3 py-1.5 transition-colors">
                {pendingAction === "booked" ? "Book" : "Shortlist"} & send
              </button>
              <button data-testid={`${idPrefix}-note-back-${a.id}`} onClick={() => { setPendingAction(null); setNote(""); }} className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/25 text-white/50 hover:text-white px-3 py-1.5 transition-colors">
                Back
              </button>
            </div>
          </div>
        ) : a.status === "booked" ? (
          <span className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-accent bg-accent text-white px-3 py-1.5">Booked</span>
        ) : a.status === "rejected" ? (
          <span className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/10 text-white/30 px-3 py-1.5">Rejected</span>
        ) : (
          <>
            {a.status === "shortlisted" ? (
              <span className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-accent text-accent px-3 py-1.5">Shortlisted</span>
            ) : (
              <button data-testid={`${idPrefix}-shortlist-${a.id}`} onClick={() => setPendingAction("shortlisted")} className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/25 hover:border-accent hover:text-accent px-3 py-1.5 transition-colors">
                Shortlist
              </button>
            )}
            <button data-testid={`${idPrefix}-book-${a.id}`} onClick={() => setPendingAction("booked")} className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-accent text-accent hover:bg-accent hover:text-white px-3 py-1.5 transition-colors">
              Book
            </button>
            <button data-testid={`${idPrefix}-reject-${a.id}`} onClick={() => onStatus(a.id, "rejected")} className="font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/25 hover:border-white hover:text-white px-3 py-1.5 transition-colors">
              Pass
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Applicants({ castingId }) {
  const [apps, setApps] = useState(null);

  useEffect(() => {
    api.get(`/employer/castings/${castingId}/applicants`).then((r) => setApps(r.data)).catch(() => setApps([]));
  }, [castingId]);

  const setStatus = async (id, status, note = "") => {
    try {
      await api.patch(`/employer/applications/${id}`, { status, note: note || undefined });
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status, response_note: note || a.response_note } : a)));
      toast.success(`Applicant ${status}.`);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (apps === null) return <p className="overline-label py-6">Loading applicants…</p>;
  if (apps.length === 0) return <p className="font-display italic text-2xl text-white/40 py-6" data-testid="no-applicants">No applicants yet.</p>;

  return (
    <div className="flex flex-col" data-testid="applicants-list">
      {apps.map((a) => <ApplicantRow key={a.id} a={a} onStatus={setStatus} />)}
    </div>
  );
}

function AllApplications() {
  const [apps, setApps] = useState(null);

  useEffect(() => {
    api.get("/employer/applications").then((r) => setApps(r.data)).catch(() => setApps([]));
  }, []);

  const setStatus = async (id, status, note = "") => {
    try {
      await api.patch(`/employer/applications/${id}`, { status, note: note || undefined });
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status, response_note: note || a.response_note } : a)));
      toast.success(status === "booked" ? "Booked — the model has been emailed." : `Applicant ${status}.`);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (!apps) return null;
  return (
    <section className="mb-16" data-testid="all-applications">
      <p className="overline-label !font-semibold !text-white/85 mb-8">All applications ({apps.length})</p>
      {apps.length === 0 ? (
        <p className="font-display italic text-2xl text-white/40">No applications yet — the board is watching.</p>
      ) : (
        <div className="panel px-6">
          {apps.map((a) => <ApplicantRow key={a.id} a={a} onStatus={setStatus} showCasting idPrefix="all-applicant" />)}
        </div>
      )}
    </section>
  );
}

export default function EmployerDashboard() {
  usePageMeta({ title: "Your Castings | Solis Casting", noindex: true });
  const { user, refresh } = useAuth();
  const [castings, setCastings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [paying, setPaying] = useState(null);
  const [editing, setEditing] = useState(null);
  const [payConfirm, setPayConfirm] = useState(null);
  const [tosAgreed, setTosAgreed] = useState(false);

  const load = () => api.get("/employer/castings").then((r) => setCastings(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const plan = user?.plan;
  const planActive = plan && !plan.on_hold && (plan.trial ? new Date(plan.trial_ends) > new Date() : true);
  const planAllowanceLeft = planActive && (plan.posts_limit === null || (plan.posts_used || 0) < plan.posts_limit);
  const atelier = planActive && plan?.key === "plan_unlimited";
  const trialDaysLeft = plan?.trial
    ? Math.max(0, Math.ceil((new Date(plan.trial_ends) - new Date()) / 86400000))
    : null;

  const publish = async (c) => {
    // Atelier: recasting an expired call is included — no checkout at all
    if (c.status === "expired" && atelier) {
      setPaying(c.id);
      try {
        await api.post(`/employer/castings/${c.id}/recast`);
        toast.success("Recast sent to the office for review — included with Atelier.");
        load();
      } catch (e) {
        toast.error(fmtErr(e));
      } finally {
        setPaying(null);
      }
      return;
    }
    const needsPayment = !(c.status === "draft" && planAllowanceLeft);
    if (needsPayment && payConfirm !== c.id) {
      setPayConfirm(c.id);
      setTosAgreed(false);
      return;
    }
    setPaying(c.id);
    try {
      if (c.status === "draft" && planAllowanceLeft) {
        const { data } = await api.post(`/employer/castings/${c.id}/publish`);
        toast.success(`Published for review — ${data.posts_limit === null ? "unlimited plan" : `${data.posts_used}/${data.posts_limit} posts used`}.`);
        await refresh();
        load();
        setPaying(null);
        return;
      }
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "casting_post",
        origin_url: window.location.origin,
        casting_id: c.id,
        agreed: true,
      });
      openExternal(data.checkout_url);
    } catch (e) {
      toast.error(fmtErr(e));
      setPaying(null);
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const removeDraft = async (c) => {
    try {
      await api.delete(`/employer/castings/${c.id}`);
      toast.success("Call deleted.");
      setDeleteConfirm(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      openExternal(data.portal_url);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const published = castings.filter((c) => c.status === "published").length;
  const applicants = castings.reduce((s, c) => s + (c.applicant_count || 0), 0);

  return (
    <div className="px-6 sm:px-10 py-24" data-testid="employer-dashboard">
      <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-16">
        <div>
          <p className="overline-label mb-4">Employer Atelier</p>
          <h1 className="font-display font-light text-4xl sm:text-6xl tracking-tighter">
            Your <span className="italic text-accent">Calls</span>
          </h1>
        </div>
        <button data-testid="new-casting-button" onClick={() => { if (!showForm) setEditing(null); setShowForm(!showForm); }} className="btn-primary">
          {showForm ? <><X size={16} /> Close</> : <><Plus size={16} /> New casting</>}
        </button>
      </Reveal>

      <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10 mb-16" data-testid="employer-stats">
        {[
          [castings.length, "Total calls"],
          [published, "Live"],
          [applicants, "Applicants"],
        ].map(([n, label]) => (
          <div key={label} className="bg-background p-8">
            <p className="font-display text-5xl mb-2">{n}</p>
            <p className="overline-label !text-[10px]">{label}</p>
          </div>
        ))}
      </div>

      {plan?.on_hold && (
        <div className="border border-accent bg-accent/10 px-8 py-6 mb-16 flex flex-wrap items-center justify-between gap-4" data-testid="hold-banner">
          <div>
            <p className="font-display text-2xl mb-1">
              Payment issue — <span className="italic text-accent">account on hold</span>
            </p>
            <p className="font-grotesk text-xs uppercase tracking-[0.15em] text-white/60">
              We couldn't charge your card · update your payment method and {plan.name} picks up where it left off
            </p>
            {plan.hold_reason ? (
              <p className="text-sm text-white/70 mt-3" data-testid="hold-reason">A note from the office: <em>"{plan.hold_reason}"</em></p>
            ) : null}
          </div>
          <button data-testid="update-payment-button" onClick={openPortal} className="btn-primary !px-6 !py-3">
            Update payment method
          </button>
        </div>
      )}

      {trialDaysLeft !== null && (
        <div className="border border-accent/40 bg-accent/5 px-8 py-6 mb-16 flex flex-wrap items-center justify-between gap-4" data-testid="trial-banner">
          <div>
            <p className="font-display text-2xl mb-1">
              Free trial — <span className="italic text-accent">{trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left</span>
            </p>
            <p className="font-grotesk text-xs uppercase tracking-[0.15em] text-white/60">
              {plan.name} · first charge on {new Date(plan.trial_ends).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} · cancel anytime via the office
            </p>
          </div>
          <p className="font-grotesk text-[11px] uppercase tracking-[0.15em] text-white/70 border border-white/20 px-4 py-2" data-testid="trial-banner-posts">
            {plan.posts_limit === null ? "Unlimited posts during trial" : `${plan.posts_limit - (plan.posts_used || 0)} of ${plan.posts_limit} posts left`}
          </p>
        </div>
      )}

      <AllApplications />

      <AffiliatePanel />

      <PlansSection />

      {showForm && <CastingForm editing={editing} onCreated={() => { setShowForm(false); setEditing(null); load(); }} />}

      <div className="flex flex-col" data-testid="employer-castings-list">
        {castings.length === 0 && (
          <p className="font-display italic text-3xl text-white/40" data-testid="no-castings">No calls yet — the board is waiting for your name.</p>
        )}
        {castings.map((c) => (
          <div key={c.id} className="panel mb-6" data-testid={`employer-casting-${c.id}`}>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-12 gap-4 items-center">
              <div className="sm:col-span-5">
                <p className="font-display text-2xl">{c.title}</p>
                <p className="overline-label !text-[10px] mt-2">{c.city} · {c.compensation || "TBD"}</p>
              </div>
              <div className="sm:col-span-2">
                <span className={`font-grotesk text-[10px] uppercase tracking-[0.15em] border px-3 py-1.5 ${STATUS_STYLE[c.status] || STATUS_STYLE.draft}`}>
                  {c.status.replace("_", " ")}
                </span>
                {c.status === "expired" && c.expired_reason === "date_passed" && (
                  <p className="font-grotesk text-[9px] uppercase tracking-[0.12em] text-accent mt-2" data-testid={`date-passed-hint-${c.id}`}>
                    Date passed — edit, then recast
                  </p>
                )}
                {c.status === "published" && c.expires_at && (
                  <p className="font-grotesk text-[9px] uppercase tracking-[0.12em] text-white/40 mt-2">
                    Live until {new Date(c.expires_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </p>
                )}
              </div>
              <div className="sm:col-span-2 overline-label !text-[10px]" data-testid={`casting-stats-${c.id}`}>
                <Eye size={12} className="inline mr-2" />{c.views || 0} views
                <span className="block mt-2"><Users size={12} className="inline mr-2" />{c.applicant_count || 0} applicants</span>
              </div>
              <div className="sm:col-span-3 flex flex-wrap gap-2 justify-end">
                {(c.status === "draft" || c.status === "expired") && payConfirm === c.id && !(c.status === "draft" && planAllowanceLeft) ? (
                  <div className="flex flex-col gap-3 items-end" data-testid={`pay-confirm-${c.id}`}>
                    <TosAgreement agreed={tosAgreed} setAgreed={setTosAgreed} testId={`publish-tos-${c.id}`} note="One-time $19.99 charge — non-refundable once the call is live." />
                    <div className="flex gap-2">
                      <button data-testid={`pay-now-${c.id}`} onClick={() => publish(c)} disabled={!tosAgreed || paying === c.id} className="btn-danger !px-5 !py-2.5 disabled:opacity-40">
                        {paying === c.id ? "Working…" : "Pay $19.99"}
                      </button>
                      <button data-testid={`pay-cancel-${c.id}`} onClick={() => setPayConfirm(null)} className="btn-outline !px-4 !py-2.5">
                        Back
                      </button>
                    </div>
                    {c.status === "expired" && !atelier && (
                      <p className="font-grotesk text-[10px] uppercase tracking-[0.12em] leading-relaxed text-accent/80 text-right max-w-[280px]" data-testid={`atelier-upsell-${c.id}`}>
                        Recasts are always free with Atelier — $349/month, unlimited posts
                      </p>
                    )}
                  </div>
                ) : (
                  <>
                    {c.status === "draft" && (
                      <button data-testid={`publish-${c.id}`} onClick={() => publish(c)} disabled={paying === c.id} className="btn-danger !px-5 !py-2.5 disabled:opacity-40">
                        {paying === c.id ? "Working…" : planAllowanceLeft ? "Publish — included" : "Publish — $19.99"}
                      </button>
                    )}
                    {c.status === "expired" && (
                      <button data-testid={`recast-${c.id}`} onClick={() => publish(c)} disabled={paying === c.id} className="btn-danger !px-5 !py-2.5 disabled:opacity-40">
                        {paying === c.id ? "Working…" : atelier ? "Recast — included with Atelier" : "Recast — $19.99"}
                      </button>
                    )}
                  </>
                )}
                {["draft", "pending_review", "published", "expired"].includes(c.status) && (
                  <button data-testid={`edit-${c.id}`} onClick={() => { setEditing(c); setShowForm(true); }} className="btn-outline !px-5 !py-2.5">
                    <Pencil size={14} /> Edit
                  </button>
                )}
                {["draft", "expired"].includes(c.status) && (deleteConfirm === c.id ? (
                  <div className="flex flex-col gap-2 items-end">
                    <div className="flex gap-2">
                      <button data-testid={`confirm-delete-draft-${c.id}`} onClick={() => removeDraft(c)} className="btn-danger !px-5 !py-2.5">
                        <Trash2 size={14} /> Confirm delete
                      </button>
                      <button data-testid={`keep-draft-${c.id}`} onClick={() => setDeleteConfirm(null)} className="btn-outline !px-4 !py-2.5">Keep</button>
                    </div>
                    {(c.applicant_count || 0) > 0 && (
                      <p className="font-grotesk text-[10px] uppercase tracking-[0.12em] text-white/45 text-right" data-testid={`delete-warn-${c.id}`}>
                        {c.applicant_count} applicant{c.applicant_count === 1 ? "" : "s"} attached — models keep their copy
                      </p>
                    )}
                  </div>
                ) : (
                  <button data-testid={`delete-draft-${c.id}`} onClick={() => setDeleteConfirm(c.id)} className="btn-outline !px-5 !py-2.5">
                    <Trash2 size={14} /> Delete
                  </button>
                ))}
                <button
                  data-testid={`view-applicants-${c.id}`}
                  onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                  className="btn-outline !px-5 !py-2.5"
                >
                  <Eye size={14} /> {expanded === c.id ? "Hide" : "Applicants"}
                </button>
              </div>
            </div>
            {expanded === c.id && <div className="px-6 pb-6"><Applicants castingId={c.id} /></div>}
          </div>
        ))}
      </div>

      <DocumentsManager hint="Contracts, call sheets — private; only the office can see these." />
      <ChangePassword />
    </div>
  );
}
